import { Component, lazy, Suspense } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { TooltipProvider } from "@headerhawk/ui";
import { AuthProvider } from "@/components/providers/auth-provider";
import { QueryProvider } from "@/components/providers/query-provider";
import { ThemeProvider } from "@/components/providers/theme-provider";
import { RequireAuth } from "@/components/providers/require-auth";
import { OnboardingScopeSync } from "@/components/providers/onboarding-scope-sync";
import { FullScreenLoader } from "@/components/ui/loader";
import { DashboardLayout } from "@/routes/dashboard/layout";
import { SettingsLayout } from "@/routes/dashboard/settings/layout";
import { OnboardingLayout } from "@/routes/onboarding/layout";

const LoginPage = lazy(() =>
  import("@/routes/login").then((m) => ({ default: m.LoginPage })),
);
const SignupPage = lazy(() =>
  import("@/routes/signup").then((m) => ({ default: m.SignupPage })),
);
const AlertEmailConfirmationPage = lazy(() =>
  import("@/routes/alert-email-confirmation").then((m) => ({
    default: m.AlertEmailConfirmationPage,
  })),
);
const DashboardPage = lazy(() =>
  import("@/routes/dashboard").then((m) => ({ default: m.DashboardPage })),
);
const IssuesPage = lazy(() =>
  import("@/routes/dashboard/issues").then((m) => ({
    default: m.IssuesPage,
  })),
);
const ReportsExplorerPage = lazy(() =>
  import("@/routes/dashboard/reports-explorer").then((m) => ({
    default: m.ReportsExplorerPage,
  })),
);
const ScriptsPage = lazy(() =>
  import("@/routes/dashboard/scripts/page").then((m) => ({
    default: m.ScriptsPage,
  })),
);
const GeneralSettingsPage = lazy(() =>
  import("@/routes/dashboard/settings").then((m) => ({
    default: m.GeneralSettingsPage,
  })),
);
const TeamSettingsPage = lazy(() =>
  import("@/routes/dashboard/settings/team").then((m) => ({
    default: m.TeamSettingsPage,
  })),
);
const BillingPage = lazy(() =>
  import("@/routes/dashboard/settings/billing").then((m) => ({
    default: m.BillingPage,
  })),
);
const SitesSettingsPage = lazy(() =>
  import("@/routes/dashboard/settings/sites").then((m) => ({
    default: m.SitesSettingsPage,
  })),
);
const AlertsSettingsPage = lazy(() =>
  import("@/routes/dashboard/settings/alerts").then((m) => ({
    default: m.AlertsSettingsPage,
  })),
);
const IntegrationsSettingsPage = lazy(() =>
  import("@/routes/dashboard/settings/integrations").then((m) => ({
    default: m.IntegrationsSettingsPage,
  })),
);
const OnboardingIndex = lazy(() =>
  import("@/routes/onboarding").then((m) => ({
    default: m.OnboardingIndex,
  })),
);
const OrgSetupStep = lazy(() =>
  import("@/routes/onboarding/org").then((m) => ({
    default: m.OrgSetupStep,
  })),
);
const TierSelectionStep = lazy(() =>
  import("@/routes/onboarding/tier").then((m) => ({
    default: m.TierSelectionStep,
  })),
);
const SiteCreationStep = lazy(() =>
  import("@/routes/onboarding/site").then((m) => ({
    default: m.SiteCreationStep,
  })),
);
const SetupInstructionsStep = lazy(() =>
  import("@/routes/onboarding/setup").then((m) => ({
    default: m.SetupInstructionsStep,
  })),
);

interface ChunkErrorBoundaryState {
  hasError: boolean;
}

class ChunkErrorBoundary extends Component<
  { children: ReactNode },
  ChunkErrorBoundaryState
> {
  state: ChunkErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ChunkErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Chunk loading failed:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4">
          <p className="text-muted-foreground">
            Failed to load page. Check your connection and try again.
          </p>
          <button
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  return (
    <ThemeProvider defaultTheme="system" storageKey="headerhawk-theme">
      <QueryProvider>
        <AuthProvider>
          <OnboardingScopeSync />
          <TooltipProvider delayDuration={300}>
            <BrowserRouter>
              <Toaster position="top-right" richColors />
              <ChunkErrorBoundary>
                <Suspense fallback={<FullScreenLoader />}>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    {/* Public: the person confirming may have no account. */}
                    <Route
                      path="/alert-email-confirmation"
                      element={<AlertEmailConfirmationPage />}
                    />
                    <Route path="/onboarding" element={<OnboardingLayout />}>
                      <Route index element={<OnboardingIndex />} />
                      <Route path="org" element={<OrgSetupStep />} />
                      <Route path="tier" element={<TierSelectionStep />} />
                      <Route path="site" element={<SiteCreationStep />} />
                      <Route path="setup" element={<SetupInstructionsStep />} />
                    </Route>
                    <Route
                      path="/dashboard"
                      element={
                        <RequireAuth>
                          <DashboardLayout />
                        </RequireAuth>
                      }
                    >
                      <Route index element={<DashboardPage />} />
                      <Route path="issues" element={<IssuesPage />} />
                      <Route path="reports" element={<ReportsExplorerPage />} />
                      <Route path="scripts" element={<ScriptsPage />} />
                      <Route path="settings" element={<SettingsLayout />}>
                        <Route index element={<GeneralSettingsPage />} />
                        <Route path="team" element={<TeamSettingsPage />} />
                        <Route path="sites" element={<SitesSettingsPage />} />
                        <Route path="alerts" element={<AlertsSettingsPage />} />
                        <Route
                          path="integrations"
                          element={<IntegrationsSettingsPage />}
                        />
                        <Route path="billing" element={<BillingPage />} />
                      </Route>
                    </Route>
                    <Route
                      path="/"
                      element={<Navigate to="/dashboard" replace />}
                    />
                    <Route
                      path="*"
                      element={<Navigate to="/dashboard" replace />}
                    />
                  </Routes>
                </Suspense>
              </ChunkErrorBoundary>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryProvider>
    </ThemeProvider>
  );
}
